import { useState, useEffect, useRef, useMemo } from 'react';
import { Card, Row, Col, Typography, Menu } from 'antd';
import {
  UserOutlined,
  SettingOutlined,
  MonitorOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  GlobalOutlined,
} from '@ant-design/icons';
import { Navbar } from '../components/website/Navbar';
import Footer from '../components/website/Footer';
import './Guides.css';

const { Title, Paragraph, Text } = Typography;

export function Guides() {
  const [activeSection, setActiveSection] = useState('account-setup');
  const sectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const guideSteps = useMemo(
    () => [
      {
        id: 'account-setup',
        title: 'Account Setup',
        description: 'Create your Statustrust account and complete your profile',
        icon: <UserOutlined />,
        content: [
          'Sign up for a free account at https://app.statustrust.io',
          'Log in using your credentials',
          'Access the dashboard to manage your services',
          'Complete your profile with company details, logo, and contact information',
        ],
      },
      {
        id: 'configure-services',
        title: 'Configure Services',
        description: 'Set up monitors for your websites, APIs, SSL, TCP Port, and PING services',
        icon: <MonitorOutlined />,
        content: [
          'Navigate to the Services page in the dashboard',
          'Click "Add New Service" to create a new monitor',
          'Fill in service details including name, URL, and authentication',
          'Configure notifications and check intervals',
          'Test the service configuration before saving',
        ],
      },
      {
        id: 'instant-checks',
        title: 'Instant Checks',
        description: 'Perform quick status checks without waiting for scheduled monitoring',
        icon: <ClockCircleOutlined />,
        content: [
          'Go to the Instant Check section in the dashboard',
          'Enter the service details you want to check',
          'Click "Check Now" to initiate the instant check',
          'View real-time results and status information',
        ],
      },
      {
        id: 'manage-downtime',
        title: 'Manage Downtime',
        description: 'Set ETAs and communicate service status during outages',
        icon: <SettingOutlined />,
        content: [
          'Navigate to Live Updates in the dashboard',
          'Click on the "Outage" or "Incident" card for the service you want to set the downtime ETA for',
          'Set expected time for service restoration',
          'Click on the "Save" button to update the downtime ETA for the service',
        ],
      },
      {
        id: 'enable-public-status-page',
        title: 'Enable Public Status Page',
        description: 'Create a public-facing status page for your services',
        icon: <GlobalOutlined />,
        content: [
          'Go to the "Settings" section in the left sidebar of the Statustrust dashboard',
          'In the Company Details tab, click on "Edit Details" and toggle the "Custom Dashboard" option to enable it',
          'Customize with your company logo and theme',
          'Preview and save your public status page',
        ],
      },
      {
        id: 'service-fields',
        title: 'Service Configuration Fields',
        description: 'Reference for all available service configuration options',
        icon: <SettingOutlined />,
        content: [],
      },
    ],
    []
  );

  const serviceFields = [
    { field: 'Name', description: 'A friendly name for the service or monitor' },
    { field: 'Description', description: 'A brief description of the service or monitor' },
    { field: 'URL', description: 'The URL or endpoint to be monitored' },
    { field: 'Authentication', description: 'Authentication method if required by the service' },
    { field: 'Notifications', description: 'Notification settings for when the service has issues' },
    { field: 'Parent Service', description: 'If this service is part of a larger service' },
    { field: 'Public Visibility', description: 'Whether the service appears on your public status page' },
    { field: 'Status', description: 'Active or inactive status for monitoring' },
    { field: 'Request Type', description: 'Type of service (HTTP, DNS, TCP, etc.)' },
    { field: 'Check Interval', description: 'How often the service should be checked' },
    { field: 'Service Logo', description: 'Image representing the service on the status page' },
    { field: 'Tags', description: 'Categories for filtering services on the status page' },
  ];

  // Scroll spy functionality
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200; // Offset for better UX

      for (const step of guideSteps) {
        const element = sectionRefs.current[step.id];
        if (element) {
          const elementTop = element.offsetTop;
          const elementBottom = elementTop + element.offsetHeight;

          if (scrollPosition >= elementTop && scrollPosition < elementBottom) {
            setActiveSection(step.id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Call once to set initial active section

    return () => window.removeEventListener('scroll', handleScroll);
  }, [guideSteps]);

  // Click handler for sidebar navigation
  const scrollToSection = (sectionId: string) => {
    const element = sectionRefs.current[sectionId];
    if (element) {
      const yOffset = -100; // Offset for fixed header
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  // Menu items for sidebar
  const menuItems = guideSteps.map(step => ({
    key: step.id,
    icon: step.icon,
    label: step.title,
    onClick: () => scrollToSection(step.id),
  }));

  return (
    <div className="guides-page">
      <Navbar />
      <div className="guides-container">
        {/* Header Section */}
        <div className="guides-header-section">
          <div className="container plr-page">
            <Title level={1}>Setup Guide</Title>
            <Paragraph className="guides-subtitle">
              Complete guide to setting up your Statustrust account, configuring services, and enabling a public status
              page for your services.
            </Paragraph>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="guides-content-wrapper">
          <div className="container plr-page">
            <Row gutter={0}>
              {/* Sidebar Navigation */}
              <Col xs={0} lg={6} className="guides-sidebar">
                <div className="sidebar-content">
                  <Title level={4}>Getting Started</Title>
                  <Menu mode="vertical" selectedKeys={[activeSection]} items={menuItems} className="guides-menu" />
                </div>
              </Col>

              {/* Main Content */}
              <Col xs={24} lg={18} className="guides-main-content">
                {guideSteps.map(step => (
                  <div
                    key={step.id}
                    id={step.id}
                    ref={el => (sectionRefs.current[step.id] = el)}
                    className="guide-section"
                  >
                    {step.id !== 'service-fields' ? (
                      <Card className="guide-step-card">
                        <div className="step-header">
                          <div className="step-icon">{step.icon}</div>
                          <Title level={2}>{step.title}</Title>
                        </div>
                        <Paragraph className="step-description">{step.description}</Paragraph>

                        {step.content.length > 0 && (
                          <ul className="step-content">
                            {step.content.map((item, itemIndex) => (
                              <li key={itemIndex}>
                                <CheckCircleOutlined className="check-icon" />
                                {item}
                              </li>
                            ))}
                          </ul>
                        )}

                        {/* Add video tutorials for specific sections */}
                        {step.title === 'Configure Services' && (
                          <div className="video-tutorial">
                            <video
                              src="https://media.statustrust.io/create+service.mp4"
                              className="tutorial-video"
                              controls
                              muted
                              autoPlay
                              loop
                              preload="metadata"
                            >
                              Your browser does not support the video tag.
                            </video>
                          </div>
                        )}
                        {step.title === 'Instant Checks' && (
                          <div className="video-tutorial">
                            <video
                              src="https://media.statustrust.io/instant+check.mp4"
                              className="tutorial-video"
                              controls
                              muted
                              autoPlay
                              loop
                              preload="metadata"
                            >
                              Your browser does not support the video tag.
                            </video>
                          </div>
                        )}
                        {step.title === 'Manage Downtime' && (
                          <div className="video-tutorial">
                            <video
                              src="https://media.statustrust.io/Service+downtime+eta.mp4"
                              className="tutorial-video"
                              controls
                              muted
                              autoPlay
                              loop
                              preload="metadata"
                            >
                              Your browser does not support the video tag.
                            </video>
                          </div>
                        )}
                        {step.title === 'Enable Public Status Page' && (
                          <div className="video-tutorial">
                            <video
                              src="https://media.statustrust.io/status+page.mp4"
                              className="tutorial-video"
                              controls
                              muted
                              autoPlay
                              loop
                              preload="metadata"
                            >
                              Your browser does not support the video tag.
                            </video>
                          </div>
                        )}
                      </Card>
                    ) : (
                      <Card className="service-fields-card">
                        <Title level={2}>Service Configuration Fields</Title>
                        <Paragraph>
                          When setting up a new service or monitor, you'll need to configure the following fields:
                        </Paragraph>
                        <Row gutter={[16, 16]}>
                          {serviceFields.map((field, index) => (
                            <Col key={index} xs={24} md={12}>
                              <Card size="small" className="field-card">
                                <Text strong>{field.field}</Text>
                                <Paragraph className="field-description">{field.description}</Paragraph>
                              </Card>
                            </Col>
                          ))}
                        </Row>
                      </Card>
                    )}
                  </div>
                ))}
              </Col>
            </Row>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
